import {
  ANNOTATION_LOCATION,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import {
  alertApiRef,
  configApiRef,
  errorApiRef,
  identityApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import CachedIcon from '@material-ui/icons/Cached';
import DeleteIcon from '@material-ui/icons/Delete';
import { useCallback, useState } from 'react';
import PaginatedTable from './PaginatedTable';

import { WorkloadClient } from '@internal/workload-api-helper';
import { useAsync } from 'react-use';
import { Account, Annotations, CpeCardProps } from '../types';
import { AboutContent } from './AboutContent';
import { MdiAws } from './aws-icon';
import { useStyles } from '../styles';
import { PdsAlert, PdsLink } from '@principal/design-system-react';

let backendHost: string;
let token: string | undefined;
let consoleDomain: string;

export function AboutCard(props: CpeCardProps) {
  const { variant } = props;

  const classes = useStyles();
  const { entity } = useEntity();

  const config = useApi(configApiRef);
  const identity = useApi(identityApiRef);

  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  const errorApi = useApi(errorApiRef);
  const [gettingAccountResources, setGettingAccountResources] =
    useState<Boolean>(false);
  const oauthApi = useApi(microsoftAuthApiRef);

  const annotations: Annotations | undefined = entity?.metadata
    ?.annotations as Annotations;
  const accountId: string | undefined =
    annotations?.['amazonaws.com/account-id'] ||
    annotations?.['azure.com/subscription-id'];

  const workloadName: string | undefined = entity.metadata?.labels?.workload!;
  const workloadClient = new WorkloadClient(config, oauthApi);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [accountResourcesNeedToBeDeleted, setAccountResourcesNeedToBeDeleted] =
    useState<boolean>(false);
  const [failedFetchAccountResources, setFailedFetchAccountResources] =
    useState<boolean>(false);
  const [accountResourcesBlockingDelete, setAccountResourcesBlockingDelete] =
    useState<{ accountId: string; name: string; arn: string; id: string }[]>();

  const [workloadData, setWorkloadData] = useState<Account>();
  const [requesterEmail, setRequesterEmail] = useState('');
  const [accountIdsInWorkload, setAccountIdsInWorkload] = useState<[string]>([
    '',
  ]);
  const [catalogLocationsInWorkload, setCatalogLocationsInWorkload] = useState<
    [string]
  >(['']);
  const [existingWorkloadDeleteIssueUrl, setExistingWorkloadDeleteIssueUrl] =
    useState<any>(undefined);

  const entityLocation = entity.metadata.annotations?.[ANNOTATION_LOCATION];
  // Limiting the ability to manually refresh to the less expensive locations
  const allowRefresh =
    entityLocation?.startsWith('url:') || entityLocation?.startsWith('file:');

  let cardClass = '';
  let cardContentClass = '';
  if (variant === 'gridItem') {
    cardClass = classes.gridItemCard;
    cardContentClass = classes.gridItemCardContent;
  } else if (variant === 'fullHeight') {
    cardClass = classes.fullHeightCard;
    cardContentClass = classes.fullHeightCardContent;
  }

  const fetchAccountData = async () => {
    return await workloadClient.apiFetch(`workloads/${accountId}`);
  };

  const fetchAccountsInWorkload = async () => {
    return await workloadClient.apiFetch(
      `workloads/?workloadName=${workloadName}`,
    );
  };

  const fetchLatestResourceData = async ({
    workload,
    resourceName,
  }: {
    workload?: string;
    resourceName?: string;
  }) => {
    return await workloadClient.apiFetch(
      `workloads/workload/${workload}/resource/${resourceName}`,
    );
  };

  const parseWorkloadObjectValues = (
    catalogLocationStringList: string[] | undefined,
  ): { [key: string]: string | undefined } => {
    const resourceFileName: string | undefined =
      catalogLocationStringList?.pop();
    const resourceName = resourceFileName?.split('.')[0];
    const workload = catalogLocationStringList?.pop();

    return {
      resourceName,
      workload,
    };
  };

  useAsync(async (): Promise<Account> => {
    backendHost = config.getString('backend.baseUrl');
    consoleDomain = config.getString('aws.console.domain');

    ({ token } = await identity.getCredentials());
    const [workloadDetails] = await Promise.all([fetchAccountData()]);
    const [details] = await Promise.all([workloadDetails.json()]);
    setWorkloadData(details);

    const [workloadAccounts] = await Promise.all([fetchAccountsInWorkload()]);
    const [workloadAccountsJson] = await Promise.all([workloadAccounts.json()]);
    const [workloadAccountIds] = await Promise.all([
      workloadAccountsJson.accounts.map((r: Account) => r.id),
    ]);
    setAccountIdsInWorkload(workloadAccountIds);

    const [workloadAccountCatalogLocations] = await Promise.all([
      workloadAccountsJson.accounts.map((r: Account) => r.catalogLocation),
    ]);
    setCatalogLocationsInWorkload(workloadAccountCatalogLocations);

    const workloadObject = parseWorkloadObjectValues(
      details?.catalogLocation!.split('/'),
    );

    const workloadResource = await fetchLatestResourceData({
      workload: workloadObject?.workload!,
      resourceName: workloadObject?.resourceName!,
    });

    const workloadResourceJson = await workloadResource.json();
    setExistingWorkloadDeleteIssueUrl(
      workloadResourceJson.spec.account_delete_issue_url!,
    );

    const { email } = await identity.getProfileInfo();
    if (email) {
      setRequesterEmail(email);
    }

    return details;
  }, []);

  const refreshEntity = useCallback(async () => {
    try {
      await catalogApi.refreshEntity(stringifyEntityRef(entity));
      alertApi.post({
        message:
          'Refresh scheduled. Wait a few seconds and refresh the page to see updated information.',
        severity: 'info',
        display: 'transient',
      });
    } catch (e: any) {
      errorApi.post({
        name: 'refresh_error',
        message:
          'Error refreshing entity. Try again or contact the Cloud Platform Engineering Team.',
        stack: e,
      });
    }
  }, [catalogApi, alertApi, errorApi, entity]);

  const GoToConsoleIcon = (workload_data: Account | any) => {
    return (
      <Tooltip title="Navigate to the AWS Console to log in to this account">
        <IconButton
          href={`https://${consoleDomain}.awsapps.com/start/#/console?account_id=${workload_data.id}`}
          target="_blank"
        >
          <MdiAws />
        </IconButton>
      </Tooltip>
    );
  };

  const RefreshIcon = () => {
    return (
      <Tooltip title="Refresh data for this resource">
        <IconButton aria-label="Refresh" title="" onClick={refreshEntity}>
          <CachedIcon />
        </IconButton>
      </Tooltip>
    );
  };

  const DeleteAccountIcon = () => {
    const [deleteRequestInProgress, setDeleteRequestInProgress] =
      useState<boolean>();
    const [workloadInputIsInErrorState, setWorkloadInputIsInErrorState] =
      useState<boolean>(true);
    const [workloadNameToDelete, setWorkloadNameToDelete] =
      useState<string>('');

    const workloadDeleteIssueUrl = entity.spec?.account_delete_issue_url;

    const getAllAccountResourcesToDelete = async (workloadIds: string[]) => {
      if (workloadIds.length > 0) {
        const combinedMap: {
          accountId: string;
          name: string;
          arn: string;
          id: string;
        }[] = [];

        const getAccountVpcResources = async (id: string) => {
          const res = await workloadClient.apiFetch(
            `workloads/${id}/features/networking/vpc-resources`,
            'GET',
          );
          if (res.status !== 200) {
            return false;
          }
          const resp = await res.json();
          return resp.resources['non-foundation-managed'];
        };

        const getAccountR53Resources = async (id: string) => {
          const res = await workloadClient.apiFetch(
            `workloads/${id}/features/networking/dns-records`,
            'GET',
          );
          if (res.status !== 200) {
            return false;
          }
          const resp = await res.json();
          return resp.resources;
        };

        const getAccountVpcResourcesToDelete = async (id: string) => {
          const non_foundation_managed_vpc_resources =
            await getAccountVpcResources(id);

          // Returns false on error, pass that to caller
          if (non_foundation_managed_vpc_resources === false) {
            return false;
          }

          const map: {
            accountId: string;
            name: string;
            arn: string;
            id: string;
          }[] = non_foundation_managed_vpc_resources.map(
            (obj: {
              name: string;
              properties: { providerUniqueId: string };
              id: string;
            }) => ({
              accountId: id,
              name: obj.name,
              arn: obj.properties.providerUniqueId!,
              id: obj.id,
            }),
          );
          return map;
        };

        const getAccountR53ResourcesToDelete = async (id: string) => {
          const non_foundation_managed_r53_resources =
            await getAccountR53Resources(id);

          // Returns false on error, pass that to caller
          if (non_foundation_managed_r53_resources === false) {
            return false;
          }

          const map: {
            accountId: string;
            name: string;
            arn: string;
            id: string;
          }[] = non_foundation_managed_r53_resources.map(
            (obj: {
              name: string;
              properties: {
                providerUniqueId: string;
                nativeType: string;
                type: string;
              };
              id: string;
            }) => ({
              accountId: id,
              name: obj.name,
              arn: `Route53 ${obj.properties.type} Record - (No ARN available)`,
              id: obj.id,
            }),
          );
          return map;
        };

        // Check for resources in all accounts in workload
        setGettingAccountResources(true);
        const accounts: any[] = [];
        await workloadIds.forEach(id => {
          accounts.push(getAccountVpcResourcesToDelete(id));
          accounts.push(getAccountR53ResourcesToDelete(id));
        });

        const resources = await Promise.all(accounts);

        // Check for errors while gathering resources
        setFailedFetchAccountResources(false);
        if (resources.includes(false)) {
          setFailedFetchAccountResources(true);
          setGettingAccountResources(false);
          return [];
        }

        combinedMap.push(...resources.flat());
        setGettingAccountResources(false);

        if (combinedMap.length > 0) {
          setAccountResourcesBlockingDelete(combinedMap);
          setAccountResourcesNeedToBeDeleted(true);
        } else {
          setAccountResourcesBlockingDelete([]);
          setAccountResourcesNeedToBeDeleted(false);
        }

        return combinedMap;
      }

      setAccountResourcesNeedToBeDeleted(false);
      return [];
    };

    const handleClickOpen = async (e: any) => {
      e.preventDefault();
      setIsModalOpen(true);
      await getAllAccountResourcesToDelete(accountIdsInWorkload);
    };

    const handleClose = () => {
      setIsModalOpen(false);
    };

    const handleDeleteRequest = async (e: any) => {
      e.preventDefault();
      await handleClickOpen(e);
    };

    const constructIssueUrl = (issue: string) => {
      return `https://github.com${issue.split('repos')[1]}`;
    };

    const patchResourceData = async ({
      workload,
      resourceName,
      payload,
    }: {
      backendHost: string;
      token?: string;
      workload?: string;
      resourceName?: string;
      payload: { [key: string]: string };
    }) => {
      return await workloadClient.apiFetch(
        `workloads/workload/${workload}/resource/${resourceName}`,
        'PATCH',
        payload,
      );
    };

    const submitIssueToGithub = async (e: any) => {
      e.preventDefault();

      setDeleteRequestInProgress(true);
      let showError = false;
      try {
        const payload: any = {
          accountIds: accountIdsInWorkload,
          requester: requesterEmail,
          changeType: 'deleteAccount',
          workload: workloadData?.workloadName!,
          submitAccountDeleteWithAttachedResources:
            accountResourcesBlockingDelete!.length > 0,
          vpcDetails: {
            vpcResourceCount: accountResourcesBlockingDelete!.length,
          },
        };

        const labels = entity?.metadata?.labels;
        const url = `workloads/workload/${labels?.workload!}`;
        const response = await workloadClient.apiFetch(url, 'DELETE', payload);

        if (response.status === 200) {
          const data = await response.json();
          const issue_url = constructIssueUrl(data.issue_url);

          // Patch catalog for every account in Workload
          for (const catalogLocation of catalogLocationsInWorkload) {
            const workloadObject = parseWorkloadObjectValues(
              catalogLocation.split('/'),
            );

            await patchResourceData({
              backendHost,
              token,
              workload: workloadObject?.workload!,
              resourceName: workloadObject?.resourceName!,
              payload: { account_delete_issue_url: issue_url },
            });
          }
          handleClose();
          await catalogApi
            .refreshEntity(stringifyEntityRef(entity))
            .then(() => {
              window.location.reload();
            });
        } else {
          showError = true;
        }
      } catch (error: any) {
        showError = true;
      }
      if (showError) {
        errorApi.post({
          name: 'delete_error',
          message:
            'Error submitting delete request. Try again or contact the Cloud Platform Engineering Team.',
        });
        showError = false;
      }

      setDeleteRequestInProgress(false);
      handleClose();
    };

    const handleSubmitDelete = (e: any) => {
      e.preventDefault();
      submitIssueToGithub(e);
    };

    const validateWorkloadNameToDelete = (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      if (workloadData?.workloadName === event.target.value) {
        setWorkloadInputIsInErrorState(false);
      } else {
        setWorkloadInputIsInErrorState(true);
      }
    };

    const handleWorkloadDeleteInputChange = (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      event.preventDefault();
      setWorkloadNameToDelete(event.target.value);
      validateWorkloadNameToDelete(event);
    };

    const ModalAttribute = (modalProps: { label: string; value: string }) => {
      return (
        <Typography>
          <Box component="span" fontWeight="bold">
            {modalProps.label}:{' '}
          </Box>
          {modalProps.value}
        </Typography>
      );
    };

    const ResourcesBlockingDelete = (resourceProps: {
      resources: {
        accountId: string;
        name: string;
        arn: string;
        id: string;
      }[];
      title: string;
      handleClose: () => void;
    }) => {
      if (resourceProps?.resources?.length > 0) {
        return (
          <>
            <Divider />
            <Box pb={2} pt={1} fontWeight="bold">
              {resourceProps.title}
            </Box>
            <PaginatedTable rows={resourceProps.resources} />
            <br />
            All resources listed above must be removed before proceeding. VPC
            and Route53 resources are scanned once per day. After removing all
            resources, please wait 24 hours before reattempting a workload
            delete request.
            <Box pt={2} display="flex" justifyContent="flex-end">
              <Button onClick={resourceProps.handleClose} color="primary">
                Close
              </Button>
            </Box>
          </>
        );
      }
      return <></>;
    };

    return (
      <>
        <Tooltip
          title={
            existingWorkloadDeleteIssueUrl !== undefined
              ? 'A workload delete request has already been submitted for this workload - click the refresh button for more details'
              : `Workload deletion available for owner members of this workload.`
          }
        >
          <span>
            <IconButton
              onClick={handleDeleteRequest}
              disabled={
                workloadDeleteIssueUrl !== undefined ||
                existingWorkloadDeleteIssueUrl !== undefined ||
                !workloadData?.ownerMembers
                  ?.filter(member => member !== null)
                  ?.map(member => member.toLowerCase())
                  .includes(requesterEmail.toLowerCase()) ||
                accountIdsInWorkload.length <= 0
              }
            >
              <DeleteIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Dialog
          maxWidth="lg"
          fullWidth
          open={isModalOpen}
          onClose={handleClose}
        >
          <form onSubmit={handleSubmitDelete} autoComplete="off">
            <DialogContent>
              <DialogTitle>Workload Delete Request</DialogTitle>
              <Divider />
              <Box pt={1}>
                <DialogContentText id="alert-dialog-description">
                  <ModalAttribute
                    label="Workload Name"
                    value={workloadData?.workloadName!}
                  />
                  <ModalAttribute
                    label="Account IDs"
                    value={accountIdsInWorkload.join(', ')}
                  />
                </DialogContentText>
              </Box>
              {(gettingAccountResources && (
                <Box pb={2}>
                  <Typography>
                    Checking for ability to delete... <LinearProgress />
                  </Typography>
                </Box>
              )) || (
                <>
                  {failedFetchAccountResources && (
                    <div
                      style={{
                        color: '#222222',
                      }}
                    >
                      <PdsAlert variant="error">
                        An error occurred while verifying this workload has no
                        remaining resources that will prevent deletion. Please
                        try again. This could have been the result of our
                        scanning timing out while looking for remaining
                        resources but the second try should be quicker as the
                        scanned resources will have been cached. However, if you
                        continue to see an error on your 3rd attempt and believe
                        you have no resources remaining in these accounts,
                        submit a request for{' '}
                        <PdsLink
                          variant="strong"
                          href="/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/principal/support/#internal-support"
                        >
                          support
                        </PdsLink>{' '}
                        to the CPE team.
                      </PdsAlert>
                    </div>
                  )}
                  {accountResourcesNeedToBeDeleted &&
                    !failedFetchAccountResources && (
                      <>
                        <DialogContentText>
                          <ResourcesBlockingDelete
                            resources={accountResourcesBlockingDelete!}
                            title="Resources Preventing Delete"
                            handleClose={handleClose}
                          />
                        </DialogContentText>
                      </>
                    )}
                  {!accountResourcesNeedToBeDeleted &&
                    !failedFetchAccountResources && (
                      <>
                        <Box pt={1}>
                          <DialogContentText id="alert-dialog-description">
                            This request will delete <b>all</b> accounts in this
                            workload. Double check the Workload Name and Account
                            IDs.
                            <br />
                            <br />
                            If they look correct, enter the Workload Name below
                            to proceed.
                          </DialogContentText>
                          <TextField
                            error={workloadInputIsInErrorState}
                            id="outlined-controlled"
                            variant="outlined"
                            value={workloadNameToDelete}
                            label="Workload Name"
                            onChange={handleWorkloadDeleteInputChange}
                          />
                          <DialogActions>
                            <Button onClick={handleClose} color="primary">
                              Cancel
                            </Button>
                            <Button
                              type="submit"
                              color="primary"
                              disabled={
                                workloadInputIsInErrorState ||
                                accountResourcesNeedToBeDeleted
                              }
                            >
                              Submit
                            </Button>
                          </DialogActions>
                        </Box>
                      </>
                    )}
                  <DialogActions>
                    {deleteRequestInProgress && (
                      <Typography>
                        Delete requested... <LinearProgress />
                      </Typography>
                    )}
                  </DialogActions>
                </>
              )}
            </DialogContent>
          </form>
        </Dialog>
      </>
    );
  };

  return (
    <Card className={cardClass}>
      <CardHeader
        title="About"
        action={
          <CardActions>
            {entity.metadata?.tags?.includes('aws') && (
              <GoToConsoleIcon {...workloadData} />
            )}
            {allowRefresh && <RefreshIcon />}
            {entity.metadata?.tags?.includes('aws') && <DeleteAccountIcon />}
          </CardActions>
        }
      />
      <Divider />
      <CardContent className={cardContentClass}>
        <AboutContent entity={entity} />
      </CardContent>
    </Card>
  );
}
